import { Button, Space, Tag, Tree, Typography } from 'antd';
import type { DataNode } from 'antd/es/tree';

import { useEffect, useState } from 'react';
import { apiClient, apiErrorMessage } from '../../../utils';
import { Grid, Integration, Role } from '../../../models';

export function CreateBackupPage(props: { onComplete: () => void }) {
  const [grids, setGrids] = useState<Grid[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [treeData, setTreeData] = useState<any[]>();
  const [checkedKeys, setCheckedKeys] = useState<string[]>(['all']);
  const [step, setStep] = useState(0);
  const [isCreating, setIsCreating] = useState(false);

  const { Text } = Typography;

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    const gridsResult = await apiClient.get('grids?includeSubGrids=true');
    const integrationsResult = await apiClient.get('integrations');
    const rolesResult = await apiClient.get('roles');

    let checkedGrids: string[] = [];
    let checkedIntegrations: string[] = [];
    let checkedRoles: string[] = [];

    const responseGrids: Grid[] = gridsResult?.data;
    if (responseGrids?.length) {
      responseGrids.forEach((grid: Grid) => {
        checkedGrids.push('grid_' + grid.id);
      });
      setGrids(responseGrids);
    }

    const responseIntegrations: Integration[] = integrationsResult?.data;
    if (responseIntegrations?.length) {
      responseIntegrations.forEach((integration: Integration) => {
        checkedIntegrations.push('integration_' + integration.id);
      });
      setIntegrations(responseIntegrations);
    }

    const responseRoles: Role[] = rolesResult?.data;
    if (responseRoles?.length) {
      responseRoles.forEach((role: Role) => {
        checkedRoles.push('role_' + role.id);
      });
      setRoles(responseRoles);
    }

    const initialCheckedKeys = ['all', 'systemSettings'];
    if (responseGrids?.length) {
      initialCheckedKeys.push('grids', ...checkedGrids);
    }
    if (responseIntegrations?.length) {
      initialCheckedKeys.push('integrations', ...checkedIntegrations);
    }
    if (responseRoles?.length) {
      initialCheckedKeys.push('roles', ...checkedRoles);
    }
    setCheckedKeys(initialCheckedKeys);
  };

  useEffect(() => {
    const titleDivStyle: React.CSSProperties = {
      marginBottom: '0.5rem',
    };

    const descriptionStyle: React.CSSProperties = {
      fontSize: '0.8rem',
      lineHeight: '0.7rem',
    };

    const newTreeData: DataNode[] = [
      {
        title: <div style={titleDivStyle}>Tümü</div>,
        key: 'all',
        children: [
          {
            title: <div style={titleDivStyle}>Veri setleri</div>,
            key: 'grids',
            disabled: grids.length <= 0,
            children:
              grids && grids.length > 0
                ? grids.map((grid: Grid) => {
                    return {
                      title: (
                        <div style={titleDivStyle}>
                          <div>{grid.name}</div>
                          <Text type="secondary" style={descriptionStyle}>
                            {grid.description}
                          </Text>
                        </div>
                      ),
                      key: 'grid_' + grid.id,
                    };
                  })
                : [
                    {
                      title: (
                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '16px' }}>
                          Sistemde herhangi bir veri seti mevcut değil.
                        </div>
                      ),
                      key: 'noGrid',
                      disabled: true,
                      checkable: false,
                    },
                  ],
          },
          {
            title: <div style={titleDivStyle}>Entegrasyonlar</div>,
            key: 'integrations',
            disabled: integrations.length <= 0,
            children:
              integrations.length > 0
                ? integrations.map((integration: Integration) => {
                    return {
                      title: (
                        <div style={titleDivStyle}>
                          <div>{integration.name}</div>
                          <Text type="secondary" style={descriptionStyle}>
                            {integration.description}
                          </Text>
                        </div>
                      ),
                      key: 'integration_' + integration.id,
                    };
                  })
                : [
                    {
                      title: (
                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '16px' }}>
                          Sistemde herhangi bir entegrasyon mevcut değil.
                        </div>
                      ),
                      key: 'noIntegration',
                      disabled: true,
                      checkable: false,
                    },
                  ],
          },
          {
            title: <div style={titleDivStyle}>Roller</div>,
            key: 'roles',
            disabled: roles.length <= 0,
            children:
              roles.length > 0
                ? roles.map((role: Role) => {
                    return {
                      title: (
                        <div style={titleDivStyle}>
                          <div>{role.name}</div>
                          <Text type="secondary" style={descriptionStyle}>
                            {role.description}
                          </Text>
                        </div>
                      ),
                      key: 'role_' + role.id,
                    };
                  })
                : [
                    {
                      title: (
                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '16px' }}>
                          Sistemde herhangi bir rol mevcut değil.
                        </div>
                      ),
                      key: 'noRole',
                      disabled: true,
                      checkable: false,
                    },
                  ],
          },
          {
            title: 'Sistem Ayarları',
            key: 'systemSettings',
          },
        ],
      },
    ];
    setTreeData(newTreeData);
  }, [Text, grids, integrations, roles]);

  const onCheck = (checkedKeys: any, info: any) => {
    setCheckedKeys(checkedKeys);
  };

  const renderCheckedItems = () => {
    const tagStyle: React.CSSProperties = { marginRight: '0.5rem', width: '7rem', textAlign: 'center' };

    const checkedItems: any[] = [];
    checkedKeys?.forEach((key: string) => {
      if (key.includes('grid_')) {
        const grid = grids?.find((g: Grid) => g.id === key.split('_')[1]);
        if (grid) {
          checkedItems.push(
            <span>
              <Tag color="green" style={tagStyle}>
                Veri Seti
              </Tag>
              {grid.name}
            </span>,
          );
        }
      } else if (key.includes('integration_')) {
        const integration = integrations?.find((i: Integration) => i.id === key.split('_')[1]);
        if (integration) {
          checkedItems.push(
            <span>
              <Tag color="blue" style={tagStyle}>
                Entegrasyon
              </Tag>
              {integration.name}
            </span>,
          );
        }
      } else if (key.includes('role_')) {
        const role = roles?.find((r: Role) => r.id === key.split('_')[1]);
        if (role) {
          checkedItems.push(
            <span>
              <Tag color="yellow" style={tagStyle}>
                Rol
              </Tag>
              {role.name}
            </span>,
          );
        }
      } else if (key === 'systemSettings') {
        checkedItems.push(
          <span>
            <Tag color="purple" style={tagStyle}>
              Sistem ayarları
            </Tag>
            Sistem ayarları
          </span>,
        );
      }
    });
    return checkedItems;
  };

  const backupSelectedItems = () => {
    setIsCreating(true);
    const itemsToBackup: any[] = [...(checkedKeys || [])];

    if (checkedKeys?.includes('all') && checkedKeys.length === 1) {
      itemsToBackup.push('systemSettings');
      grids?.forEach((grid: Grid) => {
        itemsToBackup.push('grid_' + grid.id);
      });
      integrations?.forEach((integration: Integration) => {
        itemsToBackup.push('integration_' + integration.id);
      });
      roles?.forEach((role: Role) => {
        itemsToBackup.push('role_' + role.id);
      });
    }

    const reqObj = {
      gridIds: itemsToBackup.filter((key: string) => key.includes('grid_')).map((key: string) => key.split('_')[1]),
      integrationIds: itemsToBackup.filter((key: string) => key.includes('integration_')).map((key: string) => key.split('_')[1]),
      roleIds: itemsToBackup.filter((key: string) => key.includes('role_')).map((key: string) => key.split('_')[1]),
      systemSettings: checkedKeys?.includes('systemSettings'),
    };

    apiClient
      .post('backups', reqObj)
      .then(response => {
        const element = document.createElement('a');
        const file = new Blob([JSON.stringify(response.data)], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        element.download = 'mdm-backup-' + new Date().toISOString().replace(/[-T:]/g, '').split('.')[0] + '.json';
        document.body.appendChild(element);
        element.click();
      })
      .catch(reason => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        props.onComplete();
      });
  };

  return (
    <div>
      {step === 0 && (
        <>
          <p>Yedek dosyasına dahil edilmesini istediğiniz sistem öğelerini seçin.</p>

          <Tree
            switcherIcon={<></>}
            checkable
            defaultExpandAll
            checkedKeys={checkedKeys}
            onCheck={onCheck}
            treeData={treeData}
            selectable={false}
            expandedKeys={['all', 'systemSettings', 'grids', 'integrations', 'roles']}
          />
        </>
      )}
      {step === 1 && (
        <>
          <p>Aşağıdaki öğelerin yedeği alınacak</p>
          <Space direction="vertical">{renderCheckedItems()}</Space>
        </>
      )}
      <div style={{ marginTop: '2rem', textAlign: 'right' }}>
        <Space>
          <Button
            key="back"
            onClick={() => {
              if (step === 0) {
                props.onComplete();
              } else {
                setStep(0);
              }
            }}
          >
            {step === 0 ? 'İptal' : 'Geri'}
          </Button>
          <Button
            key="submit"
            type="primary"
            disabled={(!grids?.length && !integrations?.length && !roles?.length) || !checkedKeys?.length}
            loading={isCreating}
            onClick={() => {
              if (step === 0) {
                setStep(1);
              } else {
                backupSelectedItems();
              }
            }}
          >
            {step === 0 ? 'İlerle' : 'Yedekle'}
          </Button>
        </Space>
      </div>
    </div>
  );
}
